import { supabase } from './supabase';
import { useUserStore } from '../store/userStore';
import { LETTER_PROFILS, LETTER_TYPES, type LetterProfil, type LetterType } from '../types';

/** Choix retenus dans le compte pour la lettre ; null = rien de retenu (valeur par défaut de l'écran). */
export interface LetterDefaults {
  profil: LetterProfil | null;
  type: LetterType | null;
}

export const AUCUN_DEFAUT: LetterDefaults = { profil: null, type: null };

/**
 * Lit les choix retenus. Une valeur inconnue (colonne modifiée à la main, clé retirée plus tard) est
 * traitée comme absente : l'écran ne doit jamais préremplir quelque chose qu'il ne sait pas afficher.
 * Lève si la lecture échoue (colonnes pas encore créées, réseau) : l'appelant retombe sur ses défauts.
 */
export async function getLetterDefaults(): Promise<LetterDefaults> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) return AUCUN_DEFAUT;

  const { data, error } = await supabase
    .from('profiles')
    .select('letter_profil, letter_type')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;

  const row = data as { letter_profil?: string | null; letter_type?: string | null } | null;
  return {
    profil: (LETTER_PROFILS as readonly string[]).includes(row?.letter_profil ?? '')
      ? (row?.letter_profil as LetterProfil)
      : null,
    type: (LETTER_TYPES as readonly string[]).includes(row?.letter_type ?? '')
      ? (row?.letter_type as LetterType)
      : null,
  };
}

/** Retient un ou deux choix. Seules les clés présentes dans `patch` sont écrites. */
export async function saveLetterDefaults(patch: Partial<LetterDefaults>): Promise<void> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) throw new Error('Tu dois être connecté.');

  const payload: Record<string, string> = {};
  if (patch.profil) payload.letter_profil = patch.profil;
  if (patch.type) payload.letter_type = patch.type;
  if (Object.keys(payload).length === 0) return;

  // `.select('id')` : sans lui, une mise à jour qui ne touche aucune ligne (ligne absente, droit refusé
  // par la RLS) ne renvoie AUCUNE erreur, et l'appelant croirait le choix retenu.
  const { data, error } = await supabase.from('profiles').update(payload).eq('id', userId).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Choix non enregistré.');
}
